import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, Rss, Link as LinkIcon, X, FileText, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { buildShareUrl, buildRssUrl } from '../lib/playlistShare';

const TABS = [
  { key: 'share', label: 'Share link', icon: LinkIcon },
  { key: 'rss', label: 'Podcast RSS', icon: Rss },
  { key: 'text', label: 'Copy as text', icon: FileText },
];

const PlaylistExportDialog = ({ open, onClose, episodes = [] }) => {
  const [tab, setTab] = useState('share');
  const [copied, setCopied] = useState(false);

  const ids = episodes.map((e) => e.id);
  const shareUrl = buildShareUrl(ids);
  const rssUrl = buildRssUrl(ids);
  const textList = episodes
    .map((e, i) => {
      const url = e.apple_url || (e.youtube_id ? `https://www.youtube.com/watch?v=${e.youtube_id}` : '');
      return `${i + 1}. ${e.title}${url ? `\n   ${url}` : ''}`;
    })
    .join('\n\n');

  const activeValue = tab === 'share' ? shareUrl : tab === 'rss' ? rssUrl : textList;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(activeValue);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please select and copy manually');
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({
        title: 'My AI Revolution Digest playlist',
        text: `${episodes.length} episodes I saved from AI Revolution Digest`,
        url: shareUrl,
      });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="playlist-export-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 520, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-3xl glass-strong border border-[#EF5B5B]/25 p-6 sm:p-8 relative"
            data-testid="playlist-export-dialog"
            style={{ boxShadow: '0 30px 80px -30px rgba(239,91,91,0.45)' }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full glass hover:bg-white/10 flex items-center justify-center smooth"
              data-testid="playlist-export-close"
            >
              <X className="w-4 h-4" />
            </button>

            <h3 className="text-xl sm:text-2xl font-bold mb-1">Export playlist</h3>
            <p className="text-sm text-gray-400 mb-6">
              {episodes.length} episode{episodes.length === 1 ? '' : 's'} from your library.
            </p>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 p-1 rounded-full glass border border-white/10 mb-5">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    data-testid={`playlist-export-tab-${t.key}`}
                    className={`flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm font-semibold smooth ${
                      tab === t.key ? 'bg-[#EF5B5B] text-white shadow-[0_0_18px_rgba(239,91,91,0.55)]' : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Tab body */}
            {tab === 'share' && (
              <p className="text-xs text-gray-500 mb-2">
                Anyone opening this link will be offered to import your list into their own library.
              </p>
            )}
            {tab === 'rss' && (
              <p className="text-xs text-gray-500 mb-2">
                Subscribe in Overcast, Pocket Casts, Apple Podcasts &rsaquo; Add Podcast by URL, etc.
              </p>
            )}
            {tab === 'text' && (
              <p className="text-xs text-gray-500 mb-2">
                Plain-text list with an Apple / YouTube link per episode.
              </p>
            )}

            <textarea
              value={activeValue}
              readOnly
              onFocus={(e) => e.target.select()}
              data-testid="playlist-export-textarea"
              className={`w-full ${tab === 'text' ? 'h-56' : 'h-24'} rounded-xl bg-black/50 border border-white/10 focus:border-[#EF5B5B]/60 focus:outline-none p-3 text-xs sm:text-sm text-gray-200 font-mono smooth resize-none`}
            />

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={copy}
                data-testid="playlist-export-copy"
                className="inline-flex items-center gap-2 px-5 py-3 btn-red rounded-full font-semibold text-sm"
              >
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
              {tab === 'share' && (
                <button
                  type="button"
                  onClick={nativeShare}
                  data-testid="playlist-export-native-share"
                  className="inline-flex items-center gap-2 px-5 py-3 glass border border-white/10 hover:border-[#EF5B5B]/60 rounded-full text-sm font-semibold smooth"
                >
                  <Share2 className="w-4 h-4" /> Share…
                </button>
              )}
              {tab === 'rss' && (
                <a
                  href={rssUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="playlist-export-rss-open"
                  className="inline-flex items-center gap-2 px-5 py-3 glass border border-white/10 hover:border-[#EF5B5B]/60 rounded-full text-sm font-semibold smooth"
                >
                  <Rss className="w-4 h-4" /> Open feed
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlaylistExportDialog;
